import React, { useState, useMemo, useRef, useEffect } from "react";
import CardMobile from "./CardMobile";

const CORES_TIPO_MOVIMENTO = {
  checkin: { bg: "#cffafe", cor: "#0e7490" },
  checkout: { bg: "#f3e8ff", cor: "#6b21a8" },
};

export default function PainelMovimentos({ estadias }) {
  // Filtro de Período Geral: 'futuros' (Hoje para a frente) ou 'passados' (Antes de hoje)
  const [periodoGeral, setPeriodoGeral] = useState("futuros");

  // Filtros individuais para cada coluna (Modificados para suportar droplists e texto)
  // Filtro de Tipo: permite selecionar vários tipos ao mesmo tempo (desktop e mobile)
  const [fTipos, setFTipos] = useState([]);
  const [fDataDe, setFDataDe] = useState("");
  const [fDataAte, setFDataAte] = useState("");
  const [fEta, setFEta] = useState("");
  const [fApt, setFApt] = useState("");
  const [fQuarto, setFQuarto] = useState("");
  const [fHospede, setFHospede] = useState("");
  const [fMail, setFMail] = useState("");
  const [fContacto, setFContacto] = useState("");
  const [tipoDropdownAberto, setTipoDropdownAberto] = useState(false);
  const tipoDropdownRef = useRef(null);

  // Ordenação da tabela: por padrão, "Hoje para a Frente" mostra os mais
  // próximos primeiro (ascendente) e "Movimentos Passados" mostra os mais
  // recentes primeiro (descendente).
  const [sortColuna, setSortColuna] = useState("data");
  const [sortAsc, setSortAsc] = useState(true);

  const alternarOrdenacao = (coluna) => {
    if (sortColuna === coluna) {
      setSortAsc((prev) => !prev);
    } else {
      setSortColuna(coluna);
      setSortAsc(true);
    }
  };

  useEffect(() => {
    const handleClickFora = (e) => {
      if (
        tipoDropdownRef.current &&
        !tipoDropdownRef.current.contains(e.target)
      ) {
        setTipoDropdownAberto(false);
      }
    };
    document.addEventListener("mousedown", handleClickFora);
    return () => document.removeEventListener("mousedown", handleClickFora);
  }, []);

  // Processa e unifica Check-ins e Check-outs cronologicamente
  const fluxoCronologicoCompleto = useMemo(() => {
    const listaEventos = [];
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    estadias.forEach((estadia) => {
      const isAirbnb =
        String(estadia.plataforma).trim().toUpperCase() === "AIRBNB";
      const sufixoPlataforma = isAirbnb ? "Airbnb" : "Semestre";

      // 1. EVENTO DE CHECK-IN
      if (estadia.dataEntradaEfetivaObj) {
        const dataEv = new Date(estadia.dataEntradaEfetivaObj);
        dataEv.setHours(0, 0, 0, 0);
        const isFuturo = dataEv >= hoje;

        listaEventos.push({
          idUnico: `IN_${estadia.id}`,
          tipo: `Check-In ${sufixoPlataforma}`,
          isCheckin: true,
          isFuturo,
          dataObj: dataEv,
          dataTexto: estadia.entradaEfetivaTexto,
          eta: estadia.eta || "",
          apt: estadia.apt,
          quarto: estadia.quarto,
          hospede: estadia.nome,
          numInquilinos: estadia.numeroInquilinos || "",
          email: estadia.email || "",
          contacto: estadia.contacto || "",
        });
      }

      // 2. EVENTO DE CHECK-OUT
      if (estadia.dataSaidaEfetivaObj) {
        const dataEv = new Date(estadia.dataSaidaEfetivaObj);
        dataEv.setHours(0, 0, 0, 0);
        const isFuturo = dataEv >= hoje;

        listaEventos.push({
          idUnico: `OUT_${estadia.id}`,
          tipo: `Check-out ${sufixoPlataforma}`,
          isCheckin: false,
          isFuturo,
          dataObj: dataEv,
          dataTexto: estadia.saidaEfetivaTexto,
          eta: "",
          apt: estadia.apt,
          quarto: estadia.quarto,
          hospede: estadia.nome,
          numInquilinos: estadia.numeroInquilinos || "",
          email: estadia.email || "",
          contacto: estadia.contacto || "",
        });
      }
    });

    // Ordenação Cronológica Crescente (Mais antigos -> Mais Recentes)
    return listaEventos.sort(
      (a, b) => a.dataObj.getTime() - b.dataObj.getTime()
    );
  }, [estadias]);

  // GERAÇÃO DE LISTAS ÚNICAS DINÂMICAS PARA OS DROPDOWNS
  const opcoesDropdown = useMemo(() => {
    const tipos = new Set();
    const apts = new Set();
    const quartos = new Set();

    fluxoCronologicoCompleto.forEach((ev) => {
      // Filtra as opções consoante o período ativo para o dropdown não ficar gigante
      if (periodoGeral === "futuros" && !ev.isFuturo) return;
      if (periodoGeral === "passados" && ev.isFuturo) return;

      if (ev.tipo) tipos.add(ev.tipo);
      if (ev.apt) apts.add(ev.apt.trim());
      if (ev.quarto) quartos.add(String(ev.quarto).trim());
    });

    return {
      tipos: [...tipos].sort(),
      apts: [...apts].sort(),
      quartos: [...quartos].sort((a, b) => (Number(a) || 0) - (Number(b) || 0)),
    };
  }, [fluxoCronologicoCompleto, periodoGeral]);

  // Aplicação combinada dos filtros de período e das colunas individuais
  const dadosFiltradosFinais = useMemo(() => {
    return fluxoCronologicoCompleto.filter((ev) => {
      // 1. Filtro de Período Principal
      if (periodoGeral === "futuros" && !ev.isFuturo) return false;
      if (periodoGeral === "passados" && ev.isFuturo) return false;

      // 2. Filtros Dinâmicos (Droplists e Texto)
      if (fTipos.length > 0 && !fTipos.includes(ev.tipo)) return false;
      if (fDataDe && ev.dataObj < new Date(`${fDataDe}T00:00:00`))
        return false;
      if (fDataAte && ev.dataObj > new Date(`${fDataAte}T00:00:00`))
        return false;
      if (fEta && !ev.eta.toLowerCase().includes(fEta.toLowerCase()))
        return false;
      if (fApt && ev.apt.trim() !== fApt) return false;
      if (fQuarto && String(ev.quarto).trim() !== fQuarto) return false;

      if (
        fHospede &&
        !ev.hospede.toLowerCase().includes(fHospede.toLowerCase())
      )
        return false;
      if (fMail && !ev.email.toLowerCase().includes(fMail.toLowerCase()))
        return false;
      if (
        fContacto &&
        !String(ev.contacto).toLowerCase().includes(fContacto.toLowerCase())
      )
        return false;

      return true;
    });
  }, [
    fluxoCronologicoCompleto,
    periodoGeral,
    fTipos,
    fDataDe,
    fDataAte,
    fEta,
    fApt,
    fQuarto,
    fHospede,
    fMail,
    fContacto,
  ]);

  // Ordenação final da lista, aplicada por cima dos filtros
  const dadosOrdenados = useMemo(() => {
    const copia = [...dadosFiltradosFinais];
    copia.sort((a, b) => {
      let vA, vB;
      if (sortColuna === "data") {
        vA = a.dataObj.getTime();
        vB = b.dataObj.getTime();
      } else if (sortColuna === "apt") {
        vA = a.apt.trim().toLowerCase();
        vB = b.apt.trim().toLowerCase();
      } else if (sortColuna === "quarto") {
        vA = Number(a.quarto) || 0;
        vB = Number(b.quarto) || 0;
      } else if (sortColuna === "hospede") {
        vA = a.hospede.trim().toLowerCase();
        vB = b.hospede.trim().toLowerCase();
      } else {
        vA = a.dataObj.getTime();
        vB = b.dataObj.getTime();
      }
      if (vA < vB) return sortAsc ? -1 : 1;
      if (vA > vB) return sortAsc ? 1 : -1;
      return 0;
    });
    return copia;
  }, [dadosFiltradosFinais, sortColuna, sortAsc]);

  const handleToggleTipo = (tipo) => {
    setFTipos((prev) =>
      prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo]
    );
  };

  const limparFiltros = () => {
    setFTipos([]);
    setFDataDe("");
    setFDataAte("");
    setFEta("");
    setFApt("");
    setFQuarto("");
    setFHospede("");
    setFMail("");
    setFContacto("");
  };

  return (
    <div
      style={{
        background: "#ffffff",
        padding: "24px",
        borderRadius: "16px",
        boxShadow: "0 4px 18px rgba(15, 23, 42, 0.04)",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "15px",
          borderBottom: "1px solid #f1f5f9",
          paddingBottom: "16px",
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: "700",
              color: "#0f172a",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            🗓️ Monitor Clínico de Entradas e Saídas
          </h2>
          <p
            style={{ margin: "4px 0 0 0", fontSize: "12px", color: "#64748b" }}
          >
            Total listados neste período:{" "}
            <strong>{dadosFiltradosFinais.length} movimentos</strong>
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <button
            onClick={() => {
              setPeriodoGeral("futuros");
              limparFiltros();
              setSortColuna("data");
              setSortAsc(true);
            }}
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              backgroundColor:
                periodoGeral === "futuros" ? "#06b6d4" : "#f1f5f9",
              color: periodoGeral === "futuros" ? "#ffffff" : "#475569",
            }}
          >
            📅 Hoje para a Frente
          </button>
          <button
            onClick={() => {
              setPeriodoGeral("passados");
              limparFiltros();
              setSortColuna("data");
              setSortAsc(false);
            }}
            style={{
              padding: "8px 16px",
              border: "none",
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              backgroundColor:
                periodoGeral === "passados" ? "#475569" : "#f1f5f9",
              color: periodoGeral === "passados" ? "#ffffff" : "#475569",
            }}
          >
            ↩️ Movimentos Passados
          </button>
          <button
            onClick={limparFiltros}
            style={{
              padding: "8px 12px",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              backgroundColor: "#fff",
              color: "#64748b",
              fontSize: "12px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      <div className="filtros-mobile">
        <div className="filtros-mobile__pills">
          {opcoesDropdown.tipos.map((t) => {
            const ativo = fTipos.includes(t);
            return (
              <button
                key={t}
                onClick={() => handleToggleTipo(t)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "20px",
                  border: ativo ? "1px solid #06b6d4" : "1px solid #cbd5e1",
                  backgroundColor: ativo ? "#06b6d4" : "#ffffff",
                  color: ativo ? "#ffffff" : "#475569",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
        <input
          type="text"
          placeholder="🔍 Nome do hóspede..."
          value={fHospede}
          onChange={(e) => setFHospede(e.target.value)}
          style={{
            padding: "8px",
            fontSize: "13px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
          }}
        />
        <select
          value={fApt}
          onChange={(e) => setFApt(e.target.value)}
          style={{
            padding: "8px",
            fontSize: "13px",
            borderRadius: "6px",
            border: "1px solid #cbd5e1",
            backgroundColor: "#fff",
          }}
        >
          <option value="">Apt: Todos</option>
          {opcoesDropdown.apts.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            type="date"
            value={fDataDe}
            onChange={(e) => setFDataDe(e.target.value)}
            style={{
              flex: 1,
              padding: "8px",
              fontSize: "13px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              backgroundColor: "#fff",
            }}
          />
          <input
            type="date"
            value={fDataAte}
            onChange={(e) => setFDataAte(e.target.value)}
            style={{
              flex: 1,
              padding: "8px",
              fontSize: "13px",
              borderRadius: "6px",
              border: "1px solid #cbd5e1",
              backgroundColor: "#fff",
            }}
          />
        </div>
      </div>

      <div
        className="tabela-desktop"
        style={{
          overflowX: "auto",
          width: "100%",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px",
            minWidth: "1200px",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <th
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                }}
              >
                Tipo
              </th>
              <th
                onClick={() => alternarOrdenacao("data")}
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                📅 Data{" "}
                {sortColuna === "data" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                  textAlign: "center",
                }}
              >
                ETA
              </th>
              <th
                onClick={() => alternarOrdenacao("apt")}
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                🏢 Apt.{" "}
                {sortColuna === "apt" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th
                onClick={() => alternarOrdenacao("quarto")}
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                🔑 Q.{" "}
                {sortColuna === "quarto" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                  textAlign: "center",
                }}
              >
                👥 Nº Inquilinos
              </th>
              <th
                onClick={() => alternarOrdenacao("hospede")}
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                👤 Hóspede{" "}
                {sortColuna === "hospede" ? (sortAsc ? "▲" : "▼") : ""}
              </th>
              <th
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                }}
              >
                ✉️ Mail
              </th>
              <th
                style={{
                  padding: "12px 14px",
                  color: "#475569",
                  fontWeight: "700",
                }}
              >
                📞 Contacto
              </th>
            </tr>
            {/* LINHA DE FILTROS COM DROPLISTS INTEGRADAS */}
            <tr
              style={{
                backgroundColor: "#ffffff",
                borderBottom: "2px solid #cbd5e1",
              }}
            >
              <td style={{ padding: "6px 8px", position: "relative" }}>
                <div ref={tipoDropdownRef}>
                  <button
                    onClick={() => setTipoDropdownAberto((prev) => !prev)}
                    style={{
                      width: "100%",
                      padding: "5px",
                      fontSize: "11px",
                      borderRadius: "4px",
                      border: "1px solid #cbd5e1",
                      backgroundColor: fTipos.length > 0 ? "#ecfeff" : "#fff",
                      color: fTipos.length > 0 ? "#0e7490" : "#475569",
                      fontWeight: fTipos.length > 0 ? "700" : "400",
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    {fTipos.length === 0
                      ? "Filtra..."
                      : `${fTipos.length} selecionado${
                          fTipos.length > 1 ? "s" : ""
                        }`}{" "}
                    ▾
                  </button>
                  {tipoDropdownAberto && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "8px",
                        zIndex: 20,
                        background: "#fff",
                        border: "1px solid #cbd5e1",
                        borderRadius: "6px",
                        boxShadow: "0 6px 16px rgba(15, 23, 42, 0.12)",
                        padding: "6px",
                        minWidth: "170px",
                      }}
                    >
                      {opcoesDropdown.tipos.map((t) => (
                        <label
                          key={t}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "5px 4px",
                            fontSize: "12px",
                            color: "#334155",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={fTipos.includes(t)}
                            onChange={() => handleToggleTipo(t)}
                          />
                          {t}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </td>
              <td style={{ padding: "6px 8px" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <input
                    type="date"
                    value={fDataDe}
                    onChange={(e) => setFDataDe(e.target.value)}
                    title="De"
                    style={{
                      width: "100%",
                      padding: "4px",
                      fontSize: "11px",
                      borderRadius: "4px",
                      border: "1px solid #cbd5e1",
                      backgroundColor: "#fff",
                    }}
                  />
                  <input
                    type="date"
                    value={fDataAte}
                    onChange={(e) => setFDataAte(e.target.value)}
                    title="Até"
                    style={{
                      width: "100%",
                      padding: "4px",
                      fontSize: "11px",
                      borderRadius: "4px",
                      border: "1px solid #cbd5e1",
                      backgroundColor: "#fff",
                    }}
                  />
                </div>
              </td>
              <td style={{ padding: "6px 8px" }}>
                <input
                  type="text"
                  placeholder="ETA..."
                  value={fEta}
                  onChange={(e) => setFEta(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px",
                    fontSize: "12px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e1",
                    textAlign: "center",
                  }}
                />
              </td>
              <td style={{ padding: "6px 8px" }}>
                <select
                  value={fApt}
                  onChange={(e) => setFApt(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px",
                    fontSize: "12px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="">Todos</option>
                  {opcoesDropdown.apts.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: "6px 8px", textAlign: "center" }}>
                <select
                  value={fQuarto}
                  onChange={(e) => setFQuarto(e.target.value)}
                  style={{
                    width: "65px",
                    padding: "5px",
                    fontSize: "12px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#fff",
                    textAlign: "center",
                  }}
                >
                  <option value="">Todos</option>
                  {opcoesDropdown.quartos.map((q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: "6px 8px" }} />
              <td style={{ padding: "6px 8px" }}>
                <input
                  type="text"
                  placeholder="Nome..."
                  value={fHospede}
                  onChange={(e) => setFHospede(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px",
                    fontSize: "12px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e1",
                  }}
                />
              </td>
              <td style={{ padding: "6px 8px" }}>
                <input
                  type="text"
                  placeholder="Mail..."
                  value={fMail}
                  onChange={(e) => setFMail(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px",
                    fontSize: "12px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e1",
                  }}
                />
              </td>
              <td style={{ padding: "6px 8px" }}>
                <input
                  type="text"
                  placeholder="Contacto..."
                  value={fContacto}
                  onChange={(e) => setFContacto(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "5px",
                    fontSize: "12px",
                    borderRadius: "4px",
                    border: "1px solid #cbd5e1",
                  }}
                />
              </td>
            </tr>
          </thead>
          <tbody>
            {dadosOrdenados.map((evento) => (
              <tr
                key={evento.idUnico}
                style={{ borderBottom: "1px solid #f1f5f9" }}
              >
                <td style={{ padding: "10px 14px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "11px",
                      fontWeight: "bold",
                      backgroundColor: (evento.isCheckin
                        ? CORES_TIPO_MOVIMENTO.checkin
                        : CORES_TIPO_MOVIMENTO.checkout
                      ).bg,
                      color: (evento.isCheckin
                        ? CORES_TIPO_MOVIMENTO.checkin
                        : CORES_TIPO_MOVIMENTO.checkout
                      ).cor,
                    }}
                  >
                    {evento.tipo}
                  </span>
                </td>
                <td
                  style={{
                    padding: "10px 14px",
                    fontWeight: "600",
                    textAlign: "center",
                    color: evento.isCheckin ? "#0284c7" : "#6b21a8",
                  }}
                >
                  {evento.dataTexto}
                </td>
                <td
                  style={{
                    padding: "10px 14px",
                    fontStyle: "italic",
                    textAlign: "center",
                    color: "#475569",
                  }}
                >
                  {evento.eta || "-"}
                </td>
                <td
                  style={{
                    padding: "10px 14px",
                    fontWeight: "700",
                    textAlign: "center",
                  }}
                >
                  {evento.apt}
                </td>
                <td
                  style={{
                    padding: "10px 14px",
                    fontWeight: "600",
                    textAlign: "center",
                  }}
                >
                  {evento.quarto}
                </td>
                <td
                  style={{
                    padding: "10px 14px",
                    fontWeight: "600",
                    textAlign: "center",
                  }}
                >
                  {evento.numInquilinos || "-"}
                </td>
                <td
                  style={{
                    padding: "10px 14px",
                    fontWeight: "600",
                    color: "#0f172a",
                  }}
                >
                  {evento.hospede}
                </td>
                <td style={{ padding: "10px 14px", color: "#64748b" }}>
                  {evento.email}
                </td>
                <td
                  style={{
                    padding: "10px 14px",
                    fontWeight: "500",
                    color: "#334155",
                  }}
                >
                  {evento.contacto}
                </td>
              </tr>
            ))}
            {dadosOrdenados.length === 0 && (
              <tr>
                <td
                  colSpan="9"
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#94a3b8",
                    fontWeight: "600",
                  }}
                >
                  Nenhum check-in ou checkout encontrado para os critérios
                  aplicados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="cartoes-mobile">
        {dadosOrdenados.map((evento) => {
          const corTipo = evento.isCheckin
            ? CORES_TIPO_MOVIMENTO.checkin
            : CORES_TIPO_MOVIMENTO.checkout;
          return (
            <CardMobile
              key={evento.idUnico}
              titulo={evento.hospede || "-"}
              badge={
                <span
                  style={{
                    display: "inline-block",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    fontWeight: "bold",
                    backgroundColor: corTipo.bg,
                    color: corTipo.cor,
                  }}
                >
                  {evento.tipo}
                </span>
              }
              resumo={[
                {
                  label: "Apartamento",
                  valor: `${evento.apt} · Quarto ${evento.quarto}`,
                },
                { label: "Data", valor: evento.dataTexto },
              ]}
              detalhes={[
                { label: "ETA", valor: evento.eta || "-" },
                { label: "Nº Inquilinos", valor: evento.numInquilinos || "-" },
                { label: "Email", valor: evento.email || "-" },
                { label: "Contacto", valor: evento.contacto || "-" },
              ]}
            />
          );
        })}
        {dadosOrdenados.length === 0 && (
          <p
            style={{
              padding: "40px",
              textAlign: "center",
              color: "#94a3b8",
              fontWeight: "600",
            }}
          >
            Nenhum check-in ou checkout encontrado para os critérios
            aplicados.
          </p>
        )}
      </div>
    </div>
  );
}
